import { useTranslation } from '@shared/core';
import { useMemo } from 'react';
import type { Attribute } from './product.types';

import { SimpleTextAttribute } from './components/SimpleTextAttribute';
import { DieCutIconAttribute } from './components/DieCutIconAttribute';
import { ColumnTextAttribute } from './components/ColumnTextAttribute';
import { DigitalDeliveryAttribute } from './components/premiumDigital/DigitalDeliveryAttribute';
import { FoilChoiceAttribute } from './components/FoilChoiceAttribute';
import { PaperTypeAttribute } from './components/PaperTypeAttribute';

function titleAndLineItem(value: string) {
  return {
    title: value,
    lineItem: value,
  };
}

export const useProductAttributes = (): Attribute[] => {
  const { tq } = useTranslation('stationery');

  return useMemo(
    () => [
      {
        attributeId: 'foil',
        ...titleAndLineItem(tq`Foil`),
        step: 'cardFront',
        sortOrder: 0,
        component: FoilChoiceAttribute,
        options: [
          {
            optionId: 'gold',
            value: 'GOLD',
            assetThumbnailUrl: 'https://withjoy.com/media/print/foil_color_gold.jpeg',
            ...titleAndLineItem(tq`Gold`),
          },
          {
            optionId: 'roseGold',
            value: 'ROSEGOLD',
            assetThumbnailUrl: 'https://withjoy.com/media/print/foil_color_rosegold.jpeg',
            ...titleAndLineItem(tq`Rose Gold`),
          },
          {
            optionId: 'silver',
            value: 'SILVER',
            assetThumbnailUrl: 'https://withjoy.com/media/print/foil_color_silver.jpeg',
            ...titleAndLineItem(tq`Silver`),
          },
        ],
      },
      {
        attributeId: 'paperType',
        ...titleAndLineItem(tq`Paper Type`),
        sortOrder: 1,
        component: PaperTypeAttribute,
        step: 'cardFront',
        options: [
          {
            optionId: 'signature',
            value: 'standard',
            ...titleAndLineItem(tq`Signature`),
          },
          {
            optionId: 'semiGloss',
            value: 'semiGloss',
            ...titleAndLineItem(tq`Semi-Gloss`),
          },
          {
            optionId: 'smooth',
            value: 'smooth',
            ...titleAndLineItem(tq`Smooth`),
          },
          {
            optionId: 'pearlescent',
            value: 'pearlescent',
            ...titleAndLineItem(tq`Pearlescent`),
          },
          {
            optionId: 'doubleThick',
            value: 'doubleThick',
            ...titleAndLineItem(tq`Double Thick`),
          },
        ],
      },
      {
        attributeId: 'shape',
        ...titleAndLineItem(tq`Silhouette`),
        sortOrder: 2,
        component: DieCutIconAttribute,
        step: 'cardFront',
        options: [
          {
            optionId: 'rectangle',
            value: 'rectangle',
            ...titleAndLineItem(tq`Rectangle`),
          },
          {
            optionId: 'rounded',
            value: 'rounded',
            ...titleAndLineItem(tq`Rounded`),
          },
          {
            optionId: 'arch',
            value: 'arch',
            ...titleAndLineItem(tq`Arch`),
          },
        ],
      },
      {
        attributeId: 'returnAddress',
        ...titleAndLineItem(tq`Return Address`),
        component: SimpleTextAttribute,
        componentProps: { columns: 3, minHeight: 68 },
        step: 'envelope',
        options: [
          {
            optionId: 'front',
            value: 'front',
            ...titleAndLineItem(tq`Front`),
          },
          {
            optionId: 'back',
            value: 'back',
            ...titleAndLineItem(tq`Back`),
          },
          {
            optionId: 'none',
            value: 'none',
            ...titleAndLineItem(tq`None`),
          },
        ],
      },
      {
        attributeId: 'recipientAddress',
        title: tq`Recipient Addressing`,
        lineItem: tq`Recipient Address`,
        subtitle: tq`Have your guests’ addresses printed on the front of each envelope.`,
        component: ColumnTextAttribute,
        step: 'recipientAddress',
        options: [
          {
            optionId: 'none',
            value: 'none',
            title: tq`Leave Blank`,
            lineItem: tq`None`,
          },
          {
            optionId: 'front',
            value: 'front',
            title: tq`Print Addresses`,
            lineItem: tq`Front`,
          },
        ],
      },
      {
        attributeId: 'cardBack',
        ...titleAndLineItem(tq`Card Back`),
        step: 'cardBack',
        component: null,
        options: [
          {
            optionId: 'free',
            value: '', // Placeholder
            title: tq`Free Card Back`,
            lineItem: tq`Free`,
          },
        ],
      },
      {
        attributeId: 'delivery',
        title: tq`Delivery Methods`,
        lineItem: tq`Delivery Method`,
        subtitle: tq`You'll be able to send your Digital Card via personalized email to your guests at the next step.`,
        step: 'review',
        component: DigitalDeliveryAttribute,
        options: [
          {
            optionId: 'basic',
            value: 'basic',
            valueProps: [tq`Shareable Card Link`, tq`Personalized Emails`],
            ...titleAndLineItem(tq`Card Delivery: Email Only`),
          },
          {
            optionId: 'premium',
            value: 'premium',
            valueProps: [tq`Send this card via text (USA & Canada)`, tq`Shareable Card Link`, tq`Personalized Emails`],
            ...titleAndLineItem(tq`Card Delivery: Text + Email`),
          },
          {
            optionId: 'diamond',
            value: 'diamond',
            valueProps: [tq`International Text *`, tq`Shareable Card Link`, tq`Personalized Emails`],
            ...titleAndLineItem(tq`Card Delivery: Int'l Text + Email`),
          },
        ],
      },
    ],
    [tq],
  );
};

export { useProductAttribute } from './useProductAttribute';
